import React, { FC, ReactNode } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Grid, Theme, Typography } from '@material-ui/core';
import { Checkmark, CommercialIcon, ResidentialIcon } from 'assets/svgs/led-landing';
import { Energy5Link } from 'components/energy5/atoms';
import useI18n from "evcharging/hooks/useI18n";

interface DoubleCardItem {
  image: ReactNode;
  title: string;
  text: string;
  columnDescription: Array<Array<string>>;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      overflow: 'hidden',
      padding: theme.spacing(14, 0, 7),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(28, 0, 14),
      },
    },
    mainTitle: {
      marginBottom: theme.spacing(8),
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(14),
      },
    },
    card: {
      backgroundColor: theme.palette.customColors.lightGray,
      borderRadius: theme.spacing(4),
      padding: theme.spacing(10, 10, 8),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(10, 10, 8),
      },
      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(15, 21, 12),
      },
    },
    title: {
      margin: theme.spacing(4, 0),
    },
    text: {
      marginBottom: theme.spacing(2),
    },
    bulletPoint: {
      marginTop: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
    },

    bulletText: {
      marginLeft: theme.spacing(2),
    },

    button: {
      marginTop: theme.spacing(6),
    },
  };
});

const DoubleCardsSection: FC = () => {
  const { t } = useI18n();
  const classes = useStyles();
  const data: Array<DoubleCardItem> = [
    {
      image: <ResidentialIcon />,
      title: 'landing.consumers.residential',
      text: 'landing.consumers.residential-description',
      columnDescription: [['landing.consumers.homes', 'landing.consumers.apartments', 'landing.consumers.condos']],
    },
    {
      image: <CommercialIcon />,
      title: 'landing.consumers.commercial',
      text: 'landing.consumers.commercial-description',
      columnDescription: [
        ['landing.consumers.retail', 'landing.consumers.storage', 'landing.consumers.hospitality'],
        ['landing.consumers.manufacturing', 'landing.consumers.public'],
      ],
    },
  ];
  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Typography variant="h3" className={classes.mainTitle} align="center">
          {t('landing.consumers')}
        </Typography>
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={6}
        >
          {data.map((card, index) => (
            <Grid item md={6} xs={12} key={index}>
              <Box className={classes.card}>
                {card.image}
                <Typography variant={'h2'} className={classes.title}>
                  {t(card.title)}
                </Typography>
                <Typography variant={'h6'} className={classes.text}>
                  {t(card.text)}
                </Typography>
                <Box display="flex">
                  {card.columnDescription.map((column, subIndex1) => (
                    <Box key={subIndex1} mr={subIndex1 === 0 ? 2 : 0}>
                      {column.map((text, subIndex2) => (
                        <Box className={classes.bulletPoint} key={subIndex2}>
                          <Checkmark />
                          <Typography variant={'h6'} className={classes.bulletText}>
                            {t(text)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
                <Energy5Link
                  text={t('landing.consumers.try')}
                  href="/lighting-retrofit/project-intro"
                  className={classes.button}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default DoubleCardsSection;
